import React, { Fragment, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';

import { trackV2 } from '@gumtree/shared/src/util/track-ga-event';
import { Icon } from '@gumtree/ui-library';

import { BaseAttribute, MotorsAttribute } from '@gumtree/shared/src/types/attributes';

import {
    Title,
    DescriptionBlock,
    ShowMoreLink,
    DescriptionText,
    BottomContainer,
} from './index.style';
import MotorsDisclaimer from './motors-disclaimer/motors-disclaimer';
import IdVat from './id-vat/id-vat';
import { VipState } from '../../reducers';
import SellOneLink from '../sell-one-link';
import PoweredByMotorsLogo from './powered-by-motors.svg';

const Description: React.FC<{
    isMobileCardsLayout?: boolean;
    isForSale?: boolean;
    isMotors?: boolean;
    isPetsOrPhones: boolean;
    useToggle?: boolean;
    linesToShow?: number;
    showDescriptionTitle?: boolean;
    showIcon?: boolean;
    eleId?: string;
    isUI_202506?: boolean;
}> = ({
    eleId = '',
    isUI_202506 = false,
    isMobileCardsLayout = false,
    isMotors,
    isForSale,
    isPetsOrPhones,
    useToggle = false,
    linesToShow = 3,
    showDescriptionTitle = true,
    showIcon = true,
}) => {
    const [toggle, setToggle] = useState(false);
    const {
        baseConfig,
        description,
        id,
        vatNumber,
        attributes,
        leafCategoryId,
        isFromMotorsFeed,
        userData,
    } = useSelector(
        ({
            adDetails: {
                attributes,
                description,
                id,
                vatNumber,
                l1Category,
                leafCategoryId,
                isFromMotorsFeed,
            },
            baseConfig,
            userData,
        }: VipState) => ({
            baseConfig,
            description,
            id,
            vatNumber,
            l1Category,
            leafCategoryId,
            userData,
            attributes:
                'commonAttributes' in attributes ? attributes.commonAttributes : attributes || [],
            isFromMotorsFeed,
        }),
        shallowEqual
    );

    const isUserLoggedIn = Boolean(userData?.userLoggedIn) || false;
    return (
        <DescriptionBlock id={eleId} isMobileCardsLayout={isMobileCardsLayout} isUI_202506={isUI_202506}>
            {showDescriptionTitle && (
                <Title isMobileCardsLayout={isMobileCardsLayout}>Description</Title>
            )}
            <DescriptionText
                linesToClamp={useToggle && !toggle ? linesToShow : undefined}
                itemProp="description"
            >
                {description.map((line, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Fragment key={index}>
                        {line}
                        {/* trims top line if empty */}
                        {(index !== 0 || line) && <br />}
                    </Fragment>
                ))}
            </DescriptionText>
            {toggle && (
                <div className="attribute-container">
                    {isForSale && (
                        <SellOneLink
                            isUserLoggedIn={isUserLoggedIn}
                            sellOneUrl={`${baseConfig.bffUrl}/postad/create?categoryId=${leafCategoryId}`}
                        />
                    )}
                    {!isPetsOrPhones &&
                        attributes &&
                        attributes.length > 0 &&
                        (attributes as MotorsAttribute[]).map(({ itemProp, name, value }) => (
                            <p itemProp={itemProp} key={name}>
                                {`${name}:`}
                                <b> {value}</b>
                            </p>
                        ))}
                </div>
            )}
            {((!isMobileCardsLayout && !useToggle) || toggle) && (
                <>
                    <IdVat
                        isPetsOrPhones={isPetsOrPhones}
                        postedDate={
                            (attributes as BaseAttribute[])?.find(({ key }) => key === 'Posted')
                                ?.value
                        }
                        vatNumber={vatNumber}
                        id={id}
                    />
                    {isMotors && <MotorsDisclaimer />}
                </>
            )}
            {!useToggle && isFromMotorsFeed && <PoweredByMotorsLogo />}
            {useToggle && (
                <BottomContainer>
                    {isFromMotorsFeed && <PoweredByMotorsLogo />}
                    <ShowMoreLink
                        showMore={toggle}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                            setToggle(!toggle);
                            trackV2(!toggle ? 'ReadMoreClick' : 'ReadLessClick');
                        }}
                        onKeyDown={() => setToggle(!toggle)}
                    >
                        {`Show ${!toggle ? 'more' : 'less'}`}
                        {showIcon && <Icon type="dropdown" />}
                    </ShowMoreLink>
                </BottomContainer>
            )}
        </DescriptionBlock>
    );
};

export default Description;
